// src/pages/doctor/DoctorUsers.tsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MessageSquare, QrCode } from 'lucide-react';
import DoctorMessageDialog from '../../components/DoctorMessageDialog';

interface Reservation {
  id: number;
  user_id?: number;                 // 兼容后端返回
  service: string;
  date: string;                     // "YYYY-MM-DD" or "YYYY/MM/DD"
  time: string;                     // "HH:mm"
  status: string;                   // 'assigned' | 'completed' | 'canceled' | 'doctor_cancelled' | 'expired' | 'pending' | 'reserved'
  user_email: string;
  user_name?: string;
  visit_type?: string;
  zoom_join_url?: string | null;    // 患者用
  zoom_start_url?: string | null;   // 医生用（Host）
  zoom_meeting_id?: string | null;
  is_overdue?: boolean;
  questionnaire_completed?: boolean;
  patient_chart_number?: string | null;
  user_profile?: {
    full_name?: string;
    furigana?: string;
    gender?: string;
    birth_date?: string;
    phone_number?: string;
    postal_code?: string;
    address?: string;
  };
}

interface DoctorUser {
  id: number;
  email: string | null;
  role?: string;
  provider?: string | null;         // 'email' | 'wechat' | ...
  patient_chart_number?: string | null;
  profile?: { 
    full_name?: string | null;
    phone_number?: string | null;
  } | null;

  // 兼容历史
  auth_provider?: string | null;
  wechat_openid?: string | null;
  wechat_unionid?: string | null;
}

type LoginMethod = 'email' | 'wechat' | 'line' | 'manual' | 'unknown';

interface UserRow {
  key: string;                      // 'id:xxx' | 'email:xxx'
  user_id?: number;
  email: string;
  full_name?: string;
  reservations: Reservation[];
  patient_chart_number?: string | null;
  login_method: LoginMethod;
}



const LS_KEY_EXPANDED = 'doctorUsers.expanded';
const LS_KEY_SEARCH = 'doctorUsers.search';

const safeParseJSON = <T,>(s: string | null): T | null => {
  if (!s) return null;
  try { return JSON.parse(s) as T; } catch { return null; }
};

const DoctorUsers: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState<Reservation['user_profile'] | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [msgTarget, setMsgTarget] = useState<{ reservationId: number; patientName: string } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const parseDateTime = (dateStr: string, timeStr: string): number => {
    const ds = dateStr.replace(/\./g, '/').replace(/-/g, '/');
    const [y, m, d] = ds.split('/').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    if (!y || !m || !d || isNaN(hh) || isNaN(mm)) return 0;
    return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
  };

  const inferLoginMethod = (u?: DoctorUser | null): LoginMethod => {
    if (!u) return 'unknown';
    const p = (u.provider || u.auth_provider || '').toLowerCase();
    if (p === 'wechat' || u.wechat_openid || u.wechat_unionid) return 'wechat';
    if (p === 'line') return 'line';
    if (p === 'manual') return 'manual';
    if (p === 'email') return 'email';
    // 如果没有 provider 但有 email/phone，默认 email
    if (u.email || u.profile?.phone_number) return 'email';
    // 否则认为是管理员手动录入的用户 (QR 登录)
    return 'manual';
  };

  const fetchUsers = async () => {
    try {
      const [resReservations, resAllUsers] = await Promise.all([
        api.get('/doctor/reservations', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
        api.get('/doctor/users', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
      ]);

      const now = Date.now();

      const usersById = new Map<number, DoctorUser>();
      const usersByEmail = new Map<string, DoctorUser>();
      (resAllUsers.data as DoctorUser[]).forEach((u) => {
        if (!u) return;
        if (typeof u.id === 'number') usersById.set(u.id, u);
        if (u.email) usersByEmail.set(u.email, u);
      });

      const grouped: Record<string, UserRow> = {};

      (resReservations.data as Reservation[]).forEach((orig) => {
        let r = orig;

        const key = r.user_id ? `id:${r.user_id}` : `email:${r.user_email || 'N/A'}`;
        const uMeta = r.user_id ? usersById.get(r.user_id) : (r.user_email ? usersByEmail.get(r.user_email) : undefined);

        if (!grouped[key]) {
          grouped[key] = {
            key,
            user_id: r.user_id,
            email: r.user_email || (uMeta?.email || '-'),
            full_name: r.user_name || uMeta?.profile?.full_name || undefined,
            reservations: [],
            patient_chart_number: r.patient_chart_number ?? uMeta?.patient_chart_number ?? null,
            login_method: inferLoginMethod(uMeta),
          };
        } else {
          if (!grouped[key].full_name) {
            grouped[key].full_name = r.user_name || uMeta?.profile?.full_name || undefined;
          }
          if (!grouped[key].patient_chart_number) {
            grouped[key].patient_chart_number = r.patient_chart_number ?? uMeta?.patient_chart_number ?? null;
          }
          if (!grouped[key].email || grouped[key].email === '-') {
            grouped[key].email = r.user_email || (uMeta?.email || '-');
          }
        }

        grouped[key].reservations.push(r);
      });

      Object.values(grouped).forEach((u) => {
        u.reservations.sort((a, b) => parseDateTime(b.date, b.time) - parseDateTime(a.date, a.time));
      });

      setUsers(Object.values(grouped));
    } catch (err) {
      console.error('Error fetching doctor users:', err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const params = new URLSearchParams(location.search);
    const urlEx = params.get('ex');
    const urlQ = params.get('q');

    let effectiveExpanded: string[] = [];
    const exFromUrl = safeParseJSON<string[]>(urlEx);
    if (exFromUrl && Array.isArray(exFromUrl)) effectiveExpanded = exFromUrl;
    else {
      const exFromLS = safeParseJSON<string[]>(localStorage.getItem(LS_KEY_EXPANDED));
      if (exFromLS && Array.isArray(exFromLS)) effectiveExpanded = exFromLS;
    }

    let effectiveSearch = '';
    if (typeof urlQ === 'string' && urlQ !== null) effectiveSearch = urlQ;
    else {
      const qFromLS = localStorage.getItem(LS_KEY_SEARCH);
      if (qFromLS) effectiveSearch = qFromLS;
    }

    setExpandedKeys(effectiveExpanded);
    setSearchTerm(effectiveSearch);

    let needInject = false;
    const exStr = JSON.stringify(effectiveExpanded);
    if (params.get('ex') !== exStr) needInject = true;
    if ((params.get('q') || '') !== effectiveSearch) needInject = true;
    if (needInject) {
      params.set('ex', exStr);
      params.set('q', effectiveSearch);
      navigate({ search: params.toString() }, { replace: true });
    }

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY_EXPANDED, JSON.stringify(expandedKeys));
    const params = new URLSearchParams(location.search);
    const exStr = JSON.stringify(expandedKeys);
    if (params.get('ex') !== exStr) {
      params.set('ex', exStr);
      if (params.get('q') == null) params.set('q', searchTerm);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedKeys]);

  useEffect(() => {
    localStorage.setItem(LS_KEY_SEARCH, searchTerm);
    const params = new URLSearchParams(location.search);
    if ((params.get('q') || '') !== searchTerm) {
      params.set('q', searchTerm);
      if (params.get('ex') == null) params.set('ex', JSON.stringify(expandedKeys));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => (prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]));
  };

  const renderStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="text-green-600 font-semibold text-sm">✓ {t('doctor.completed')}</span>;
      case 'doctor_cancelled':
        return <span className="text-yellow-600 font-semibold text-sm">{t('mypage.doctor_cancelled')}</span>;
      case 'canceled':
        return <span className="text-gray-500 font-semibold text-sm">{t('mypage.user_cancelled')}</span>;
      case 'expired':
        return <span className="text-red-500 font-semibold text-sm">{t('mypage.expired')}</span>;
      case 'assigned':
      case 'reserved':
      case 'pending':
      default:
        // Use a more specific check if we had access to the reservation object here, 
        // but since renderStatusLabel only takes status, we might need to handle is_overdue elsewhere 
        // or change the signature. For now, we'll handle the red color in the map loop.
        return <span className="text-yellow-600 font-semibold text-sm">{t('doctor.incomplete')}</span>;
    }
  };

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.full_name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, searchTerm]);

  const getBestReservationId = (u: UserRow): number | null => {
    const preferred = u.reservations.find(r => !isCancelled(r.status) && r.status !== 'expired');
    return (preferred ?? u.reservations[0])?.id ?? null;
  };

  const extractProfile = (u: UserRow): Reservation['user_profile'] | null => {
    for (const r of u.reservations) {
      if (r.user_profile) return r.user_profile;
    }
    return null;
  };

  const renderLoginIcon = (method: LoginMethod) => {
    if (method === 'wechat') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100" title="WeChat">
          <MessageSquare size={14} /> WeChat
        </span>
      );
    }
    if (method === 'line') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-[#00B900]/10 text-[#00B900] font-semibold border border-[#00B900]/20" title="LINE">
          <MessageSquare size={14} /> LINE
        </span>
      );
    }
    if (method === 'email') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-slate-50 text-slate-700 font-semibold border border-slate-200" title="Email">
          <Mail size={14} /> Email
        </span>
      );
    }
    if (method === 'manual') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-amber-50 text-amber-600 font-semibold border border-amber-100" title="QR Login">
          <QrCode size={14} /> {t('admin.login_qr') || 'QR登录'}
        </span>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  // 取消类状态：不显示「診療記録」
  const isCancelled = (s: string) => s === 'canceled' || s === 'doctor_cancelled';
  // 如需连同过期也禁用，加上 || s === 'expired'

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="text-xl font-bold">{t('doctor.tab_users')}</div>

        <div className="w-full sm:w-72">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('doctor.search_user_placeholder') || 'Search by email or name'}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      <div className="bg-white bg-opacity-95 p-2 md:p-4 rounded-xl shadow-lg overflow-x-auto">
        {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み）。
            外側テーブル(利用者一覧)と、展開時の内側テーブル(予約一覧)の両方を、
            ここで縦積みのカードとして再現する。内側テーブルは外側 md:table の
            配下にあり、モバイルでは親ごと display:none になるため、
            予約一覧のモバイル表示はこのカード内で行う。 */}
        <div className="md:hidden divide-y">
          {filteredUsers.map((u) => {
            const rid = getBestReservationId(u);
            return (
              <div key={u.key} className="py-3">
                {/* 氏名 + ログイン方法 */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800 break-words">
                      {u.full_name || t('doctor.no_name')}
                    </div>
                    <div className="text-xs text-gray-500 break-all mt-0.5">
                      {t('doctor.user_email')}：{u.email || '-'}
                    </div>
                  </div>
                  <div className="shrink-0">{renderLoginIcon(u.login_method)}</div>
                </div>

                {/* カルテ番号 / 予約数 */}
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('doctor.chart_number') || 'カルテ番号'}：</span>
                    <span className="text-gray-700 break-all">{u.patient_chart_number || '-'}</span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-400">{t('doctor.appointment_count') || 'Appointments'}：</span>
                    <span className="text-gray-700">{u.reservations.length}</span>
                  </div>
                </div>

                {/* 操作: プロフィール / 予約展開 / メッセージ */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      const p = extractProfile(u);
                      setProfileData(p);
                      setShowProfileModal(true);
                    }}
                    className="shrink-0 bg-gray-200 text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-300"
                  >
                    {t('doctor.view_profile')}
                  </button>
                  <button
                    onClick={() => toggleExpand(u.key)}
                    className="shrink-0 bg-coral text-white px-3 py-1 rounded-lg text-sm hover:bg-coral"
                  >
                    {expandedKeys.includes(u.key)
                      ? t('doctor.collapse_reservations')
                      : t('doctor.show_reservations')}
                  </button>
                  {rid && (
                    <button
                      onClick={() => setMsgTarget({ reservationId: rid, patientName: u.full_name || u.email })}
                      className="shrink-0 flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700 transition"
                    >
                      <MessageSquare size={13} />
                      メッセージ
                    </button>
                  )}
                </div>

                {/* 展開時: 予約一覧（内側テーブルのモバイル版カード） */}
                {expandedKeys.includes(u.key) && (
                  <div className="mt-3 space-y-2">
                    {u.reservations.map((r) => (
                      <div key={r.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        {/* 日付・時間 + 状態 */}
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="font-semibold text-gray-800 text-sm">
                            {r.date} {r.time}
                          </div>
                          <div className={`text-xs shrink-0 ${r.is_overdue ? 'text-red-500 font-bold' : ''}`}>
                            {renderStatusLabel(r.status)}
                            {r.is_overdue && ` (${t('status.overdue_short')})`}
                          </div>
                        </div>

                        {/* 診療内容 */}
                        <div className="mt-1.5 text-xs text-gray-700 break-words">
                          <span className="text-gray-400">{t('doctor.service')}：</span>
                          {t(`service.${r.service}`)}
                          {r.visit_type && (
                            <span className="ml-1 text-gray-500">
                              ({t(`visit_type.${r.visit_type}`)})
                            </span>
                          )}
                        </div>

                        {/* ビデオ通話 */}
                        <div className="mt-2 text-xs">
                          <span className="text-gray-400">{t('doctor.video_call')}：</span>
                          {r.status === 'assigned' && (r.zoom_join_url || r.zoom_start_url) ? (
                            <div className="mt-1 flex flex-col gap-1">
                              {r.zoom_meeting_id && (
                                <div className="text-[10px] text-gray-500 font-mono break-all">ID: {r.zoom_meeting_id}</div>
                              )}
                              <div className="flex gap-1 flex-wrap">
                                {r.zoom_join_url && (
                                  <a
                                    href={r.zoom_join_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 bg-coral text-white px-3 py-1 rounded-lg hover:bg-coral text-sm inline-block font-semibold"
                                  >
                                    {t('doctor.enter_consultation') || '入室する'}
                                  </a>
                                )}
                                {r.zoom_start_url && (
                                  <a
                                    href={r.zoom_start_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 border border-coral text-coral px-2 py-1 rounded-lg hover:bg-coral/5 text-sm inline-block font-semibold"
                                    title="Host Link"
                                  >
                                    {t('doctor.host_start') || 'Host'}
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>

                        {/* 診療記録 */}
                        <div className="mt-2 text-xs">
                          <span className="text-gray-400">{t('doctor.medical_record')}：</span>
                          {isCancelled(r.status) ? (
                            <span className="text-gray-400">-</span>
                          ) : (
                            <button
                              onClick={() => navigate(`/doctor/record/${r.id}`)}
                              className="text-coral underline"
                            >
                              {['assigned'].includes(r.status) ? t('doctor.fill_record') : t('doctor.view')}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {u.reservations.length === 0 && (
                      <div className="text-center py-3 text-gray-400 text-sm">
                        {t('doctor.no_reservations')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filteredUsers.length === 0 && (
            <div className="text-center py-4 text-gray-400 text-sm">
              {t('doctor.no_users')}
            </div>
          )}
        </div>

        {/* ▼ デスクトップ: テーブル表示 */}
        <table className="hidden md:table min-w-full table-auto text-sm md:text-base">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center">
              <th className="px-2 py-1">{t('doctor.user_email')}</th>
              <th className="px-2 py-1">{t('doctor.user_name')}</th>
              <th className="px-2 py-1">{t('doctor.chart_number') || 'カルテ番号'}</th>
              <th className="px-2 py-1">{t('doctor.login_method') || 'ログイン'}</th>
              <th className="px-2 py-1">{t('doctor.appointment_count') || 'Appointments'}</th>
              <th className="px-2 py-1">{t('doctor.medical_record')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <React.Fragment key={u.key}>
                <tr className="border-t hover:bg-gray-50 text-center">
                  <td className="px-2 py-1">{u.email || '-'}</td>
                  <td className="px-2 py-1">{u.full_name || t('doctor.no_name')}</td>
                  <td className="px-2 py-1">{u.patient_chart_number || '-'}</td>
                  <td className="px-2 py-1">{renderLoginIcon(u.login_method)}</td>
                  <td className="px-2 py-1">{u.reservations.length}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <button
                        onClick={() => {
                          const p = extractProfile(u);
                          setProfileData(p);
                          setShowProfileModal(true);
                        }}
                        className="bg-gray-200 text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-300"
                      >
                        {t('doctor.view_profile')}
                      </button>
                      <button
                        onClick={() => toggleExpand(u.key)}
                        className="bg-coral text-white px-3 py-1 rounded-lg text-sm hover:bg-coral"
                      >
                        {expandedKeys.includes(u.key)
                          ? t('doctor.collapse_reservations')
                          : t('doctor.show_reservations')}
                      </button>
                      {(() => {
                        const rid = getBestReservationId(u);
                        if (!rid) return null;
                        return (
                          <button
                            onClick={() => setMsgTarget({ reservationId: rid, patientName: u.full_name || u.email })}
                            className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700 transition"
                          >
                            <MessageSquare size={13} />
                            メッセージ
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>

                {expandedKeys.includes(u.key) && (
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="p-0">
                      <div className="overflow-x-auto">
                        {/* デスクトップのみ表示（モバイルは上部カード内の予約一覧で代替） */}
                        <table className="hidden md:table w-full table-auto text-sm md:text-base border">
                          <thead>
                            <tr className="bg-gray-200 text-gray-700 text-center">
                              <th className="px-2 py-1 w-28">{t('doctor.date')}</th>
                              <th className="px-2 py-1 w-20">{t('doctor.time')}</th>
                              <th className="px-2 py-1 w-48">{t('doctor.service')}</th>
                              <th className="px-2 py-1 w-28">{t('doctor.status')}</th>
                              <th className="px-2 py-1 w-36">{t('doctor.video_call')}</th>
                              <th className="px-2 py-1 w-40">{t('doctor.medical_record')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {u.reservations.map((r) => (
                              <tr key={r.id} className="border-t text-center">
                                <td className="px-2 py-1">{r.date}</td>
                                <td className="px-2 py-1">{r.time}</td>
                                <td className="px-2 py-1">
                                  {t(`service.${r.service}`)}
                                  {r.visit_type && (
                                    <span className="ml-1 text-gray-500 text-xs">
                                      ({t(`visit_type.${r.visit_type}`)})
                                    </span>
                                  )}
                                </td>
                                <td className={`px-2 py-1 ${r.is_overdue ? 'text-red-500 font-bold' : ''}`}>
                                  {renderStatusLabel(r.status)}
                                  {r.is_overdue && ` (${t('status.overdue_short')})`}
                                </td>

                                {/* 医生端：仅 assigned 且有 start_url 时显示进入按钮 */}
                                <td className="px-2 py-1">
                                  {r.status === 'assigned' && (r.zoom_join_url || r.zoom_start_url) ? (
                                    <div className="flex flex-col items-center gap-1">
                                      {r.zoom_meeting_id && (
                                        <div className="text-[10px] text-gray-500 font-mono">ID: {r.zoom_meeting_id}</div>
                                      )}
                                      <div className="flex gap-1">
                                        {r.zoom_join_url && (
                                          <a
                                            href={r.zoom_join_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-coral text-white px-3 py-1 rounded-lg hover:bg-coral text-sm inline-block font-semibold"
                                          >
                                            {t('doctor.enter_consultation') || '入室する'}
                                          </a>
                                        )}
                                        {r.zoom_start_url && (
                                          <a
                                            href={r.zoom_start_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="border border-coral text-coral px-2 py-1 rounded-lg hover:bg-coral/5 text-sm inline-block font-semibold"
                                            title="Host Link"
                                          >
                                            {t('doctor.host_start') || 'Host'}
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-sm">-</span>
                                  )}
                                </td>

                                <td className="px-2 py-1">
                                  <div className="flex items-center justify-center gap-2">
                                    {isCancelled(r.status) ? (
                                      <span className="text-gray-400 text-sm">-</span>
                                    ) : (
                                      <button
                                        onClick={() => navigate(`/doctor/record/${r.id}`)}
                                        className="text-coral underline"
                                      >
                                        {['assigned'].includes(r.status) ? t('doctor.fill_record') : t('doctor.view')}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {u.reservations.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-center py-3 text-gray-400">
                                  {t('doctor.no_reservations')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-4 text-gray-400">
                  {t('doctor.no_users')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* メッセージダイアログ */}
      {msgTarget && (
        <DoctorMessageDialog
          reservationId={msgTarget.reservationId}
          patientName={msgTarget.patientName}
          onClose={() => setMsgTarget(null)}
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96 shadow-lg">
            <h2 className="text-lg font-bold mb-4">{t('profile.patient_info')}</h2>
            {profileData ? (
              <div className="text-sm space-y-1">
                <div>{t('doctor.name')}: {profileData.full_name || t('na')}</div>
                <div>{t('doctor.furigana')}: {profileData.furigana || t('na')}</div>
                <div>{t('doctor.gender')}: {profileData.gender || t('na')}</div>
                <div>{t('doctor.birthday')}: {profileData.birth_date || t('na')}</div>
                <div>{t('doctor.phone')}: {profileData.phone_number || t('na')}</div>
                <div>{t('doctor.postal_code')}: {profileData.postal_code || t('na')}</div>
                <div>{t('doctor.address')}: {profileData.address || t('na')}</div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">{t('doctor.no_profile') || 'No profile available.'}</div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowProfileModal(false)}
                className="px-4 py-2 bg-coral text-white rounded-lg hover:bg-coral/90 transition"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DoctorUsers;
